import React, { useState } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import {
  ModalInjectedProps,
  Modal,
  Button,
  LoadingPlaceholder,
  Flex,
  Box,
  useModalContext,
  Text,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import { ModalStyled, ErrorIcon } from './PartialShareModal.styles'
import { SettingsTab } from './SettingsTab'
import { ContentsTab } from './ContentsTab'
import {
  PreviewFormValues,
  PreviewFormProvider,
  usePreviewForm,
} from './usePreviewForm'
import {
  useCreatePreviewMutation,
  GetSharePreviewQuery,
  useDeletePreviewMutation,
  useUpdatePreviewMutation,
  useGetSharePreviewQuery,
} from '@sketch/gql-types'
import { ModalConfirmation } from './ModalConfirmation'
import { PreviewCreatedSuccessModal } from './PreviewCreatedSuccessModal'

const validationSchema = yup.object({
  previewName: yup.string().required('Preview name is required'),
  description: yup.string(),
  password: yup.string().when(['passwordProtected', 'hasExistingPassword'], {
    is: (passwordProtected: boolean, hasExistingPassword: boolean) =>
      passwordProtected && !hasExistingPassword,
    then: schema =>
      schema
        .required('Password is required when password protection is enabled')
        .min(6, 'Password must be at least 6 characters'),
    otherwise: schema =>
      schema.test(
        'password-change',
        'Password must be at least 6 characters',
        value => !value || value.length >= 6
      ),
  }),
  expiryDate: yup.string().when('expiryDateEnabled', {
    is: true,
    then: schema =>
      schema.required('Date is required when preview expiration is enabled'),
  }),
  selectedUUIDs: yup
    .array(
      yup.object({
        uuid: yup.string().required(),
        type: yup.string().oneOf(['PAGE', 'PROTOTYPE']).required(),
      })
    )
    .min(1, 'At least one item must be selected'),
})

// Use the preview type from the GraphQL query
export type PreviewType = NonNullable<GetSharePreviewQuery['share']>['preview']

const formatSelectedUUIDs = (existingPreview?: PreviewType) => {
  const pages =
    existingPreview?.pages.entries
      .filter(item => item.uuid !== null)
      .map(item => ({
        type: 'PAGE' as const,
        uuid: item.uuid!,
      })) || []
  const prototypes =
    existingPreview?.prototypes.entries.map(item => ({
      type: 'PROTOTYPE' as const,
      uuid: item.uuid,
    })) || []

  return [...pages, ...prototypes]
}

const getInitialValues = (
  existingPreview?: PreviewType
): PreviewFormValues => ({
  previewName: existingPreview?.name || '',
  description: existingPreview?.description || '',
  inspectEnabled: existingPreview?.inspectEnabled || false,
  commentsEnabled: existingPreview?.commentsEnabled || false,
  passwordProtected: !!existingPreview?.passwordProtected,
  password: '',
  hasExistingPassword: !!existingPreview?.passwordProtected,
  expiryDate: existingPreview?.expiryDate
    ? new Date(existingPreview.expiryDate).toISOString().split('T')[0]
    : '',
  expiryDateEnabled: !!existingPreview?.expiryDate,
  selectedUUIDs: formatSelectedUUIDs(existingPreview) || [],
})

interface PartialShareModalProps extends ModalInjectedProps {
  shareName: string
  shareIdentifier: string
  shareThumbnail?: string
  versionShortId: string
  existingPreviewId?: string
}

export const PartialShareModal = ({
  shareName,
  shareIdentifier,
  shareThumbnail,
  versionShortId,
  existingPreviewId,
  hideModal,
}: PartialShareModalProps) => {
  const { showToast } = useToast()
  const { showModal } = useModalContext()
  const isEditMode = !!existingPreviewId
  const [previewIdentifier, setPreviewIdentifier] = useState<
    string | undefined
  >(existingPreviewId)

  const { data, loading, error, refetch } = useGetSharePreviewQuery({
    variables: { previewIdentifier: previewIdentifier || '', shareIdentifier },
    skip: !previewIdentifier,
  })

  const existingPreview = data?.share?.preview

  const [createPreview] = useCreatePreviewMutation({
    onCompleted: ({ createPreview }) => {
      showModal(PreviewCreatedSuccessModal, {
        previewId: createPreview.preview.identifier,
      })
    },
    onError: 'show-toast',
  })

  const [updatePreview] = useUpdatePreviewMutation({
    onCompleted: ({ updatePreview }) => {
      showToast(
        <>
          The Preview &quot;{updatePreview.preview.name}&quot; has been
          successfully updated.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const [deleteMutation] = useDeletePreviewMutation({
    onCompleted: ({ deletePreview }) => {
      showToast(
        <>
          The Preview &quot;{deletePreview.preview.name}&quot; has been
          successfully deleted.
        </>
      )

      hideModal()
    },
    onError: 'show-toast',
  })

  const handleRemovePreview = () => {
    if (!previewIdentifier) {
      return
    }

    deleteMutation({
      variables: {
        identifier: previewIdentifier,
      },
    })
  }

  const handleSubmit = (values: PreviewFormValues) => {
    const expiryDate =
      values.expiryDateEnabled && !!values.expiryDate
        ? new Date(values.expiryDate).toISOString()
        : undefined

    if (isEditMode && previewIdentifier) {
      // Only include password in the mutation if the user has entered a new one
      const hasNewPassword = values.password.trim() !== ''

      updatePreview({
        variables: {
          identifier: previewIdentifier,
          name: values.previewName,
          description: values.description,
          inspectEnabled: values.inspectEnabled,
          commentsEnabled: values.commentsEnabled,
          expiryDate,
          // Only include password if user entered a new one, otherwise omit to preserve existing password
          ...(hasNewPassword && { password: values.password.trim() }),
          // although we're dealing with UUIDs, the mutation expects them to be under identifier
          content: values.selectedUUIDs.map(selected => ({
            identifier: selected.uuid,
            type: selected.type,
          })),
        },
      })
      return
    }

    return createPreview({
      variables: {
        shareIdentifier,
        name: values.previewName,
        description: values.description,
        inspectEnabled: values.inspectEnabled,
        commentsEnabled: values.commentsEnabled,
        expiryDate,
        password: values.password.trim(),
        // although we're dealing with UUIDs, the mutation expects them to be under identifier
        content: values.selectedUUIDs.map(selected => ({
          identifier: selected.uuid,
          type: selected.type,
        })),
      },
    })
  }

  if (loading) {
    return (
      <ModalStyled onCancel={hideModal}>
        <Flex justifyContent="center" alignItems="center" height={635}>
          <LoadingPlaceholder size="64px" />
        </Flex>
      </ModalStyled>
    )
  }

  if (error) {
    return (
      <ModalStyled onCancel={hideModal}>
        <Flex
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
          height={635}
          p={5}
        >
          <ErrorIcon />
          <Text pt={4} pb={4} textAlign="center" fontSize="F">
            Failed to load preview settings
          </Text>
          <Flex>
            <Button onClick={hideModal}>Cancel</Button>
            <Button variant="primary" onClick={() => refetch()}>
              Try Again
            </Button>
          </Flex>
        </Flex>
      </ModalStyled>
    )
  }

  return (
    <Formik
      initialValues={getInitialValues(existingPreview)}
      validationSchema={validationSchema}
      validateOnMount={true}
      onSubmit={handleSubmit}
    >
      <ModalStyled onCancel={hideModal}>
        <PreviewFormProvider
          previewIdentifier={previewIdentifier}
          setPreviewIdentifier={setPreviewIdentifier}
        >
          <Modal.Body>
            <Modal.Tabs
              steps={[
                {
                  title: 'Settings',
                  content: (
                    <SettingsTab
                      shareName={shareName}
                      isEditMode={isEditMode}
                      shareThumbnail={shareThumbnail}
                      shareIdentifier={shareIdentifier}
                    />
                  ),
                },
                {
                  title: 'Preview Contents',
                  content: (
                    <ContentsTab
                      shareIdentifier={shareIdentifier}
                      versionShortId={versionShortId}
                    />
                  ),
                },
              ]}
            />
          </Modal.Body>
          <PreviewFormFooter
            onRemovePreview={handleRemovePreview}
            hideModal={hideModal}
            isEditMode={isEditMode}
          />
        </PreviewFormProvider>
      </ModalStyled>
    </Formik>
  )
}

function PreviewFormFooter({
  hideModal,
  onRemovePreview,
  isEditMode,
}: {
  hideModal: () => void
  onRemovePreview: () => void
  isEditMode: boolean
}) {
  const { submitForm, isSubmitting, isValid, dirty } = usePreviewForm()
  const { showModal } = useModalContext()

  const handleRemove = () => {
    showModal(ModalConfirmation, {
      title: 'Remove Preview?',
      description:
        'The link to this preview will no longer work and no-one will be able to access it.',
      confirmText: 'Remove Preview',
      onConfirm: onRemovePreview,
    })
  }

  return (
    <Modal.Footer
      leftContent={
        isEditMode ? (
          <Box ml={4}>
            <Button
              variant="negative-secondary"
              disabled={isSubmitting}
              onClick={handleRemove}
            >
              Remove Preview
            </Button>
          </Box>
        ) : undefined
      }
    >
      <Button disabled={isSubmitting} onClick={hideModal}>
        Cancel
      </Button>
      <Button
        variant="primary"
        onClick={submitForm}
        // submit is disabled until there are changes, when in edit mode
        disabled={isSubmitting || !isValid || (isEditMode && !dirty)}
      >
        {isSubmitting
          ? isEditMode
            ? 'Updating...'
            : 'Creating...'
          : isEditMode
            ? 'Update Preview'
            : 'Create Preview'}
      </Button>
    </Modal.Footer>
  )
}
